import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { FieldUpdateHandlerParam } from 'shared/form';
import { FormLayoutContent, FormLayout } from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import FiltersForm from 'routes/Filters/components/FiltersForm';
import { Ref } from 'routes/Filters/components/FiltersForm/FiltersForm';
import * as filtersSelectors from 'data/filters/filters.selectors';
import * as filterGroupSelectors from 'data/filterGroup/filterGroup.selectors';
import { RootState } from 'data/root.reducers';
import * as filtersActions from 'data/filters/filters.actions';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import * as securityInfoActions from 'data/securityInfo/securityInfo.actions';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import { Filters } from 'data/filters/filters.types';
import { filtersTemplate } from 'routes/Filters/templates/filtersTemplate';
import {
 adaptFormDataForView, adaptTemplateForType, adaptTemplateOptions, adaptTemplateVisibilityMode
} from 'routes/Filters/utils/filtersTemplate.adapter';

type RouterParams = {
  history: string
}
const mapState = (state: RootState) => ({
  deliveryFunctionId: state.global.selectedDF,
  selectedFilters: filtersSelectors.getSelectedFilters(state)[0],
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  originalSelectedFilters: filtersSelectors.getSelectedFilters(state)[0],
  filters: filtersSelectors.getFilters(state),
  filterGroupList: filterGroupSelectors.getFilterGroupList(state),
});

const mapDispatch = {
  getSecurityInfos: securityInfoActions.getSecurityInfos,
  goToFiltersList: filtersActions.goToFiltersList,
  goToFiltersEdit: filtersActions.goToFiltersEdit,
  getFilters: filtersActions.getFilters,
};
const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter;

export const FiltersView = ({
  history,
  selectedFilters,
  deliveryFunctionId,
  userId,
  userName,
  isAuditEnabled,
  goToFiltersList,
  goToFiltersEdit,
  getFilters,
  filterGroupList,
}: Props): JSX.Element => {

  const formRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<Partial<Filters>>({});
  const [template, setTemplate] = useState(adaptTemplateVisibilityMode(filtersTemplate, 'view'));
  const { t } = useTranslation();

  const id = selectedFilters?.id;

  useEffect(() => {
    if (id) {
      getFilters(id);
    }
  }, [id, getFilters]);

  useEffect(() => {
    if (selectedFilters != null && template != null) {
      const clonedTemplate = cloneDeep(template);
      clonedTemplate.formId = Date.now().toString();
      let adaptedFormData = cloneDeep(formData);
      const adaptedTemplate = adaptTemplateOptions(filterGroupList, clonedTemplate);
      if (selectedFilters.type) {
        adaptTemplateForType(adaptedTemplate, selectedFilters.type);
        adaptedFormData = adaptFormDataForView(selectedFilters, adaptedFormData);
      }
      setFormData({
        ...selectedFilters,
        ...adaptedFormData
      });
      setTemplate(adaptedTemplate);
    }
  }, [selectedFilters, id, filterGroupList]);

  const handleCancel = () => goToFiltersList(history);

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
    if (formData != null) {
      return;
    }

    const clonedFormData = cloneDeep(formData as Filters);

    setFormData({
      ...clonedFormData,
      [fieldName]: value,
    });
  };

  const renderForm = () => {
    if (formData && template) {
      return (
        <FiltersForm
          ref={formRef}
          data={formData}
          template={template}
          onFieldUpdate={handleFieldUpdate}
        />
      );
    }
  };

  const viewFiltersTitle = t('filter.view.title');
  const renderFormPanel = (formTitle:string) => {
    return (
      <FormPanel title={viewFiltersTitle}>
        <FormButtons
          isEditing={false}
          onClickCancel={handleCancel}
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };

  return (
    <FormLayout>
      <FormLayoutContent>
        {renderFormPanel(t('ss8Distributor.view.title'))}
        {renderForm()}
      </FormLayoutContent>
    </FormLayout>
  );
};

export default withRouter(connector(FiltersView));
